import Plyr from 'plyr';
import type { Ref } from 'vue';
import Hls from 'hls.js';

export function usePlayer(videoElRef: Ref<HTMLVideoElement | null>) {
  let hlsInstance: Hls | null = null;
  let plyrInstance: Plyr | null = null;

  function destroy() {
    if (hlsInstance) {
      hlsInstance.destroy();
      hlsInstance = null;
    }
    if (plyrInstance) {
      plyrInstance.destroy();
      plyrInstance = null;
    }
  }

  /** 播放普通视频（MP4/WebM，通过 Range 请求） */
  function playNative(src: string): void {
    destroy();

    const el = videoElRef.value;
    if (!el) return;

    el.src = src;
    el.load();
    el.play().catch(() => { /* autoplay blocked */ });

    // 初始化 Plyr
    plyrInstance = new Plyr(el, {
      controls: [
        'play-large', 'play', 'progress', 'current-time', 'duration',
        'mute', 'volume', 'captions', 'settings', 'pip', 'airplay', 'fullscreen',
      ],
      settings: ['quality', 'speed'],
    });
  }

  /** 播放 HLS 流 */
  function playHls(videoId: string): void {
    destroy();

    const el = videoElRef.value;
    if (!el) return;

    const playlistUrl = `/video/${videoId}/playlist.m3u8`;

    if (Hls.isSupported()) {
      hlsInstance = new Hls({
        maxBufferLength: 30,
        enableWorker: true,
        lowLatencyMode: false,
      });
      hlsInstance.loadSource(playlistUrl);
      hlsInstance.attachMedia(el);

      hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
        el.play().catch(() => { /* autoplay blocked */ });
      });

      hlsInstance.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data.type, data.details);
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hlsInstance?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hlsInstance?.recoverMediaError();
              break;
            default:
              destroy();
              break;
          }
        }
      });
    } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari 原生 HLS
      el.src = playlistUrl;
      el.load();
      el.play().catch(() => { /* autoplay blocked */ });
    }

    // 初始化 Plyr
    plyrInstance = new Plyr(el, {
      controls: [
        'play-large', 'play', 'progress', 'current-time', 'duration',
        'mute', 'volume', 'pip', 'airplay', 'fullscreen',
      ],
    });
  }

  return { playNative, playHls, destroy };
}
